import { useUnmount } from 'ahooks';
import { useContext, useMemo, useRef } from 'react';
import tinycolor from 'tinycolor2';
import { formatColor, type GradientColorResult } from '@meitu/saas-colors/dist/esm/utils/color-convert';
import { TextLayerExtension } from '@meitu/saas-editor-core/es/editor/layer-extensions/text-layer';
import { TextEditPlugin } from '@meitu/saas-editor-core/es/plugins/text-edit';
import { type ILayerProps, type ITextLayerProps, type ITexture } from '@meitu/saas-editor-core/es/version-types';
import { ColorGroup } from '@meitu/xdesign-widgets';
import { TextureOptions } from '@meitu/xdesign-widgets/dist/colorGroup';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { checkIsInArtTextGeneratorMode } from '@/utils/mode';
import { TextPanelContext } from '../../context';
import { useEditingRange, useLayerProps, useTextContent } from '../../hooks';
import { type TexturePanelRenderFn } from '../../types';
import { Tracker } from '../../utils';

type Props = {
    useForFill?: boolean;
    size?: { width: number; height: number };
    panelPosition?: string;
    renderTexturePanel?: TexturePanelRenderFn;
};

const FALLBACK_COLOR = '#000000ff';

const formatTextGradientColor = (textGradient: GradientColorResult) => {
    const gradient = {
        ...textGradient,
        colors: textGradient.colors.map((item) => {
            return {
                color: tinycolor(item.color).toHex8String(),
                value: item.value,
            };
        }),
    };
    return formatColor(gradient) as string;
};

export const TextColorPicker: React.FC<Props> = ({ useForFill = false, size, renderTexturePanel, panelPosition }) => {
    const { layerId, recordId } = useContext(TextPanelContext)!;

    const editor = useEditor();
    const props = useLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        const { color, textGradient, texture, shapeExtension, advancedTextMaterial } = layerData;

        return {
            color,
            textGradient,
            texture,
            shapeExtension,
            advancedTextMaterial,
        };
    });
    const popoverWidgetRef = useGlobalWidget(editor, false);

    const color = props ? props.color : FALLBACK_COLOR;
    const textGradient = props ? props.textGradient : null;
    const texture = props ? props.texture : undefined;
    const shapeExtension = props ? props.shapeExtension : null;
    const advancedTextMaterial = props ? props.advancedTextMaterial : null;

    const isInArtTextGeneratorMode = checkIsInArtTextGeneratorMode();
    const ignoreRange = !isInArtTextGeneratorMode && !!advancedTextMaterial;

    const editingRange = useEditingRange(editor);
    const textContent = useTextContent(editor, editingRange);
    const textContentColor = textContent ? textContent.color : undefined;

    const finalColor = useMemo(() => {
        if (textContentColor) return textContentColor;
        if (textGradient && textGradient.colors.length > 0) return formatTextGradientColor(textGradient);
        return color;
    }, [color, textGradient, textContentColor]);

    const disableGradient = (editingRange != null && !editingRange.isCollapsed) || !!shapeExtension;
    const disableTexture = (editingRange != null && !editingRange.isCollapsed) || !!shapeExtension;

    // ATTENTION: ColorGroup 设计有问题（textureData必须要有值），textureData 先按照旧项目中定义的方式传入
    const textureData = useMemo<ITexture | undefined>(() => {
        if (disableTexture) return undefined;

        return {
            name: texture?.name,
            url: texture?.url ?? '',
            stretch: texture?.stretch ?? TextureOptions()?.[0].value,
            scale: texture?.scale ?? 1,
        };
    }, [texture, disableTexture]);

    const unlockFn = useRef<(() => void) | undefined>(undefined);
    const originLayer = useRef<ILayerProps | null>(null);

    const handleColorPickerOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;

        if (open) {
            Tracker.openTextColorPicker({
                recordId,
                stageType: editor.stage.getType(),
                isFill: useForFill,
                ...(panelPosition ? { panelPosition } : {}),
            });

            unlockFn.current = editor.getInstance(TextEditPlugin).lockEditingRange();
        } else {
            if (unlockFn.current) {
                unlockFn.current();
                unlockFn.current = undefined;
            }
        }
    };

    const handleChangeColor = (color: string) => {
        if (originLayer.current == null) {
            originLayer.current = editor.stage.getLayer(layerId, true);
        }

        if (isInArtTextGeneratorMode) {
            return editor.getInstance(TextLayerExtension).adjustArtTextSingleCharacter(layerId, { color });
        }

        editor.getInstance(TextLayerExtension).setFillColor(layerId, color, { ignoreRange });
    };

    const handleAfterChangeColor = (color: string) => {
        if (isInArtTextGeneratorMode) {
            editor
                .getInstance(TextLayerExtension)
                .adjustArtTextSingleCharacter(layerId, { color }, { writeHistory: true });
            originLayer.current = null;
            return;
        }

        editor.getInstance(TextLayerExtension).setFillColor(layerId, color, {
            originLayer: originLayer.current ? originLayer.current : undefined,
            ignoreRange,
        });

        originLayer.current = null;

        Tracker.setTextColor({
            recordId,
            stageType: editor.stage.getType(),
            color,
            isFill: useForFill,
            ...(panelPosition ? { panelPosition } : {}),
        });
    };

    const handleChangeTexture = (texture: ITexture) => {
        if (!texture.url) return;

        editor.getInstance(TextLayerExtension).setTexture(layerId, texture);
        Tracker.setTexture({
            recordId,
            stageType: editor.stage.getType(),
            texture,
            ...(panelPosition ? { panelPosition } : {}),
        });
    };

    useUnmount(() => {
        if (unlockFn.current) {
            unlockFn.current();
            unlockFn.current = undefined;
        }
    });

    return (
        <ColorGroup
            isGradient={!disableGradient}
            isTexture={!disableTexture}
            textureData={textureData}
            placement="left"
            showIcon={!useForFill}
            popupTriggerSize={size || { width: 92, height: 34 }}
            color={finalColor}
            onOpenChange={handleColorPickerOpenChange}
            setColor={handleChangeColor}
            setColorAfter={(color, _, { isBrandColor }) => {
                handleAfterChangeColor(color);
                if (isBrandColor) {
                    window._stat_track?.('hb_layers_func_use', {
                        func_id: '1_107',
                    });
                }
            }}
            textureChange={handleChangeTexture}
            onToggleBizTab={(bizTab) => {
                if (bizTab === 'team') {
                    window._stat_track?.('hb_layers_func_click', {
                        func_id: '1_107',
                    });
                }
            }}
            popupOverlayClassName="text-panel-color-picker"
            selectTextureImageModal={renderTexturePanel}
        />
    );
};
